import {
    ChangeEvent,
    FunctionComponent,
    ReactElement,
    SyntheticEvent,
    useCallback,
    useState,
} from "react";

import {
    Autocomplete,
    AutocompleteInputChangeReason,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
    styled,
} from "@mui/material";

import { Close } from "@mui/icons-material";

import * as Yup from "yup";
import { PlusCircle } from "@phosphor-icons/react";
import { useFormik } from "formik";

import { useDynamicAutocomplete } from "../../hooks";
import { useMessage } from "../../sdk/hooks";

import { AssetContainer } from "./AssetContainer";

const StyledDialog = styled(Dialog)``;

const StyledDialogContent = styled(DialogContent)`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled(Typography)`
    font-size: 18px;
    font-weight: 500;
`;

const StyledDialogTitle = styled(DialogTitle)`
    width: 100%;
    background-color: #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => theme.spacing(1, 2)};
`;

const StyledDialogActions = styled(DialogActions)`
    padding: ${({ theme }) => theme.spacing(1, 3, 3, 3)};
`;

const UploadSection = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    border: 1px dashed rgba(191, 191, 191, 1);
    padding: ${({ theme }) => theme.spacing(2)};
`;

const AddIcon = styled(PlusCircle)`
    color: black;
    cursor: pointer;
`;

const UploadAssetsTitle = styled(Typography)`
    color: grey;
    font-size: 14px;
`;

const FileUploadLabel = styled("label")`
    cursor: pointer;
    margin: ${({ theme }) => theme.spacing(1)};
`;

const FileUploadInput = styled("input")`
    display: none;
`;

const StyledForm = styled("form")``;

const acceptedFileFormats = ["jpg", "jpeg", "png", "mp4", "mov"];

const tagOptions = [
    "listing",
    "plp",
    "main-desktop",
    "main-mobile",
    "carousel-1-desktop",
    "carousel-1-mobile",
    "carousel-2-desktop",
    "carousel-2-mobile",
    "carousel-3-desktop",
    "carousel-3-mobile",
    "primary",
    "care",
    "footer-desktop",
    "footer-mobile",
];

export interface IUploadAssetsDialogProps {
    open: boolean;
    onClose: () => void;
    onFileSave: (formValues: IUploadAssetsFormValues) => void;
}

export interface IUploadAssetsFormValues {
    files: File[];
    tags: string | null;
    position: number;
    header: string;
    description: string;
    altText: string;
}

const validationSchema = Yup.object({
    files: Yup.array()
        .min(1, "Please select at least one file")
        .required("Please select at least one file"),
    tags: Yup.string().nullable(),
    position: Yup.number()
        .min(1, "Position must be at least 1")
        .required("Position is required"),
    header: Yup.string(),
    description: Yup.string(),
    altText: Yup.string(),
});

export const UploadAssetsDialog: FunctionComponent<IUploadAssetsDialogProps> = (
    props: IUploadAssetsDialogProps,
): ReactElement => {
    const { open, onClose, onFileSave } = props;
    const { showError } = useMessage();

    const { options, handleInputChange } = useDynamicAutocomplete(tagOptions);

    const formik = useFormik<IUploadAssetsFormValues>({
        initialValues: {
            files: [],
            tags: null,
            position: 1,
            header: "",
            description: "",
            altText: "",
        },
        validationSchema,
        onSubmit: (values) => {
            if (values.files.length > 0) {
                onFileSave(values);
                onClose();
            } else {
                showError("Please select at least one file before uploading");
            }
        },
    });

    const handleFileUpload = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const { files } = event.target;
            if (files) {
                const newFiles = Array.from(files).filter((file) => {
                    const extension =
                        file.name.split(".").pop()?.toLowerCase() ?? "";
                    if (!acceptedFileFormats.includes(extension)) {
                        showError(
                            `The file type "${file.name}" is not accepted.`,
                        );
                        return false;
                    }
                    return true;
                });
                formik.setFieldValue("files", [
                    ...formik.values.files,
                    ...newFiles,
                ]);
            }
        },
        [formik, showError],
    );

    const handleDeleteFile = useCallback(
        (index: number) => {
            const updatedFiles = formik.values.files.filter(
                (_, fileIndex) => fileIndex !== index,
            );
            formik.setFieldValue("files", updatedFiles);
        },
        [formik],
    );

    const handleTagChange = useCallback(
        (_event: SyntheticEvent, value: string | null) => {
            formik.setFieldValue("tags", value);
        },
        [formik],
    );

    return (
        <StyledDialog
            maxWidth="xs"
            fullWidth
            disableScrollLock
            open={open}
            onClose={onClose}>
            <StyledDialogTitle>
                <Title>Upload Assets</Title>
                <IconButton onClick={onClose}>
                    <Close />
                </IconButton>
            </StyledDialogTitle>
            <StyledForm onSubmit={formik.handleSubmit}>
                <StyledDialogContent>
                    <Autocomplete
                        size="small"
                        options={options}
                        getOptionLabel={(option) => option}
                        value={formik.values.tags}
                        onChange={handleTagChange}
                        onInputChange={handleInputChange}
                        freeSolo
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Section"
                                margin="none"
                                placeholder="Select or type a tag"
                                error={
                                    formik.touched.tags && !!formik.errors.tags
                                }
                                helperText={
                                    formik.touched.tags && formik.errors.tags
                                }
                            />
                        )}
                    />
                    <TextField
                        fullWidth
                        label="Position"
                        variant="outlined"
                        type="number"
                        name="position"
                        value={formik.values.position}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        inputProps={{ min: 1 }}
                        margin="none"
                        error={
                            formik.touched.position && !!formik.errors.position
                        }
                        helperText={
                            formik.touched.position && formik.errors.position
                        }
                    />
                    <TextField
                        fullWidth
                        label="Header"
                        variant="outlined"
                        name="header"
                        value={formik.values.header}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        margin="none"
                        error={formik.touched.header && !!formik.errors.header}
                        helperText={
                            formik.touched.header && formik.errors.header
                        }
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        variant="outlined"
                        name="description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        margin="none"
                        error={
                            formik.touched.description &&
                            !!formik.errors.description
                        }
                        helperText={
                            formik.touched.description &&
                            formik.errors.description
                        }
                    />
                    <TextField
                        fullWidth
                        label="Alt Text"
                        variant="outlined"
                        name="altText"
                        value={formik.values.altText}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        margin="none"
                        error={
                            formik.touched.altText && !!formik.errors.altText
                        }
                        helperText={
                            formik.touched.altText && formik.errors.altText
                        }
                    />
                    <UploadSection>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center">
                            {formik.values.files.length > 0 && (
                                <Box>
                                    {formik.values.files.map((file, index) => (
                                        <AssetContainer
                                            key={index}
                                            file={file}
                                            index={index}
                                            onDeleteAsset={() =>
                                                handleDeleteFile(index)
                                            }
                                        />
                                    ))}
                                </Box>
                            )}
                            <FileUploadLabel htmlFor="file-upload">
                                <AddIcon size={25} weight="duotone" />
                            </FileUploadLabel>
                            <UploadAssetsTitle>
                                Upload images or videos (jpg, png, MP4, mov)
                            </UploadAssetsTitle>
                            <FileUploadInput
                                id="file-upload"
                                type="file"
                                accept=".jpg,.jpeg,.png,video/mp4,video/x-m4v,video/*"
                                multiple
                                onChange={handleFileUpload}
                            />
                        </Box>
                    </UploadSection>
                </StyledDialogContent>
                <StyledDialogActions>
                    <Button variant="outlined" color="error" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        disabled={formik.isSubmitting || !formik.isValid}>
                        Upload
                    </Button>
                </StyledDialogActions>
            </StyledForm>
        </StyledDialog>
    );
};
